/**
 * ImpersonationBanner - Persistent sticky banner shown when a platform admin
 * is viewing the app as another user. Provides visual indicator, countdown
 * timer, and exit button.
 */

import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Eye, LogOut, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import { endImpersonation } from '../../../services/api/impersonationService';
import { media } from '../../../styles/media';
import { bannerBaseCss, bannerIconCss, bannerLabelCss } from '../bannerStyles';

// ---------------------------------------------------------------------------
// Countdown helpers
// ---------------------------------------------------------------------------

function getRemainingSeconds(expiresAt: string): number {
  return Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
}

function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const BannerRoot = styled.div`
  ${bannerBaseCss}
  z-index: ${({ theme }) => theme.zIndex.modal + 10};
  background: #b45309;

  ${media.tablet} {
    flex-wrap: wrap;
  }
`;

const EyeIcon = styled(Eye)`
  ${bannerIconCss}
`;

const Label = styled.span`
  ${bannerLabelCss}
  flex: 1;
`;

interface CountdownBadgeProps {
  $urgent: boolean;
}

const CountdownBadge = styled.span<CountdownBadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-variant-numeric: tabular-nums;
  background: ${({ $urgent }) => ($urgent ? '#dc2626' : 'rgba(255,255,255,0.2)')};
  transition: background 300ms ease-in-out;
  min-width: 70px;
  justify-content: center;
`;

const ClockIcon = styled(Clock)`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
`;

const ExitButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: transparent;
  color: #ffffff;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast};
  min-height: 32px;
  min-width: 44px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: #ffffff;
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ImpersonationBanner = () => {
  const impersonation = useAuthStore((s) => s.impersonation);
  const setImpersonation = useAuthStore((s) => s.setImpersonation);
  const navigate = useNavigate();
  const expiresAt = impersonation?.expiresAt;

  const [remainingSeconds, setRemainingSeconds] = useState<number>(() =>
    expiresAt ? getRemainingSeconds(expiresAt) : 0,
  );
  const [isExiting, setIsExiting] = useState(false);

  // Dep is the ISO string, not the object reference — avoids teardown/restart when
  // the store writes the same session data with a new object identity.
  useEffect(() => {
    if (!expiresAt) return;

    const interval = setInterval(() => {
      const secs = getRemainingSeconds(expiresAt);
      setRemainingSeconds(secs);
      if (secs === 0) {
        clearInterval(interval);
        setImpersonation(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, setImpersonation]);

  const handleExit = useCallback(async () => {
    if (!impersonation) return;
    setIsExiting(true);

    try {
      await endImpersonation(impersonation.sessionId);
    } catch {
      // Best-effort: clear even if API call fails so UI isn't stuck
    } finally {
      setImpersonation(null);
      navigate('/platform/users');
    }
  }, [impersonation, setImpersonation, navigate]);

  if (!impersonation) return null;

  const { targetUserName, targetOrgName } = impersonation;
  const displayName = targetOrgName ? `${targetUserName} (${targetOrgName})` : targetUserName;
  const isUrgent = remainingSeconds < 60;

  return (
    <BannerRoot role="region" aria-label="Impersonation active" aria-live="polite">
      <EyeIcon aria-hidden="true" />
      <Label>Viewing as {displayName}</Label>

      <CountdownBadge $urgent={isUrgent} aria-label={`Session expires in ${formatCountdown(remainingSeconds)}`}>
        <ClockIcon aria-hidden="true" />
        {formatCountdown(remainingSeconds)}
      </CountdownBadge>

      <ExitButton
        type="button"
        onClick={handleExit}
        disabled={isExiting}
        aria-label="Exit impersonation session"
      >
        <LogOut aria-hidden="true" />
        {isExiting ? 'Exiting…' : 'Exit'}
      </ExitButton>
    </BannerRoot>
  );
};
